import React, { Component } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import Measure from 'react-measure';
import ReactGA from 'react-ga';
import { detect } from 'detect-browser';

import Router from './components/Router';
import Spinner from './components/Spinner';
import getQuestions from './api/getQuestions';

class App extends Component {
  constructor(props) {
    super(props);

    this.state = {
      currentView: 'question',
      currentQuestion: 0,
      step: 1,
      answers: [],
      score: 0,
      contentHeight: -1,
      slideDirection: 'left',
      resultsPageSlug: '',
      questionsData: [],
      totalQuestions: 0
    };

    this.entryId = window.entryId;
    this.entrySlug = window.entrySlug;

    this.boundGetQuestions = getQuestions.bind(this);

    const browser = detect();

    // There's a bug in Safari 12 that breaks the slide animation — use a fade instead
    this.animationClassNames =
      (browser.name === 'safari' && browser.version.substring(0, 2) === '12') || (browser.name === 'ios' && browser.version.substring(0, 2) === '12') ? 'fade' : 'slide';

    // Init analytics
    ReactGA.initialize('UA-36583197-1');
  }

  async componentDidMount() {
    // Ajax request to API
    await this.boundGetQuestions(window.entryId);

    // One step per question view and one per answer
    this.totalSteps = this.state.totalQuestions * 2;

    ReactGA.event({
      category: `quiz-${this.entrySlug}`,
      action: 'view',
      label: 'quiz-viewed'
    });
  };

  goTo = (viewName, step = 0) =>
    this.setState({
      currentView: viewName,
      step: step
    });

  handleAnswer = event => {
    const target = event.target;
    const value = parseInt(target.value);
    const name = target.name;
    const answerIndex = target.getAttribute('data-answer-index');
    const answerText = target.getAttribute('data-answer-text');
    const question = target.getAttribute('data-question-number');

    ReactGA.event({
      category: `quiz-${this.entrySlug}`,
      action: 'click',
      label: `${question}: ${answerText}`,
      value: value
    });

    this.setState(
      {
        currentView: 'answer',
        // If there are more steps to go, increment step
        step:
          this.state.step < this.totalSteps
            ? this.state.step + 1
            : this.state.step,
        // Add the new answer to the answers array
        answers: [
          ...this.state.answers,
          {
            name: name,
            value: value,
            answerIndex: answerIndex
          }
        ],
        // Loop through the answers array, adding up the values
        score: this.state.answers.reduce((count, o) => count + o.value, 0) + value,
        slideDirection: 'left'
      },
      // Callback: navigate to the next question or the results page
      () => {
        this.goTo(
          this.state.currentView,
          this.state.step
        );
      }
    );
  };

  goToNextQuestion = () => {
    // Navigate from an answer page to the next question
    // Increment question - but only if this isn't the last answer
    const questionNumber = this.state.currentQuestion < this.state.questionsData.length
    ? this.state.currentQuestion + 1
    : this.state.currentQuestion;

    // If this is the last question, navigate to the results page
    // and DON'T increment step
    if(this.state.step === this.totalSteps) {
      ReactGA.event({
        category: `quiz-${this.entrySlug}`,
        action: 'click',
        label: 'quiz-completed',
        value: this.state.score
      });

      this.setState(
        {
          currentView: 'result',
          slideDirection: 'left'
        },
        // Callback: navigate to the next view
        () => {
          this.goTo(
            this.state.currentView,
            this.state.step
          );
        }
      )
    } else {
      this.setState(
        {
          step:
            // If there's a question for the next step, increment step
            this.state.step < this.totalSteps
              ? this.state.step + 1
              : this.state.step,
          currentQuestion: questionNumber,
          currentView: 'question',
          slideDirection: 'left'
        },
        // Callback: navigate to the next view
        () => {
          this.goTo(
            this.state.currentView,
            this.state.step
          );
        }
      );
    }
  }

  render() {
    // Return a loader while we wait for the data from the API
    return this.state.questionsData.length === 0
    ? (
        <div className="u-container u-mx-auto u-max-w-lg u-min-h-64 u-border u-border-solid u-border-orange-500 u-p-4 u-bg-white u-overflow-hidden u-bg-white u-flex u-flex-col u-items-center">
          <p className="u-font-bold u-text-orange-500 u-text-xl">Fetching quiz…</p>
          <Spinner />
        </div>
      )
    : (
      <div className="u-container u-mx-auto u-max-w-lg u-p-4 u-bg-white u-bg-white u-overflow-hidden">
        <div
          className={
            'u-mt-0 u-relative u-transition u-transition-slow u-transition-min-height u-slide-' +
            this.state.slideDirection
          }
          style={{ minHeight: this.state.contentHeight }}
        >
          <TransitionGroup>
            <CSSTransition
              key={this.state.currentView}
              timeout={500}
              classNames={this.animationClassNames}
            >
              <Measure
                bounds
                onResize={contentRect => {
                  // Content height should only increase, not decrease
                  this.setState({
                    contentHeight:
                      contentRect.bounds.height > this.state.contentHeight
                        ? contentRect.bounds.height
                        : this.state.contentHeight
                  });
                }}
              >
                {({ measureRef }) => (
                  <Router
                    currentQuestion={this.state.currentQuestion}
                    goTo={this.goTo}
                    handleAnswer={this.handleAnswer}
                    goToNextQuestion={this.goToNextQuestion}
                    // goBack={this.goBack}
                    innerRef={measureRef}
                    currentView={this.state.currentView}
                    questionsData={this.state.questionsData}
                    question={this.state.questionsData[this.state.currentQuestion]}
                    totalQuestions={this.state.totalQuestions}
                    answer={
                      this.state.answers.length > 0
                        ? this.state.answers[this.state.currentQuestion]
                        : null
                    }
                    answers={this.state.answers}
                    score={this.state.score}
                    resultsPageSlug={this.state.resultsPageSlug}
                    step={this.state.step}
                    entrySlug={this.entrySlug}
                  />
                )}
              </Measure>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    );
  }
}

export default App;
